import React, { useContext } from 'react';
import { Box, Typography, Link, useMediaQuery, useTheme } from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import LogoUploadIcon from '../../../assets/icons/LogoUpload.svg';

const Copyright = ({ clinicData, onUploadLogoClick }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { userDataStore } = useContext(StoreContext);
    const userData = userDataStore?.userData || {};
    const subscriptionData = userDataStore?.subscriptionData || {};

    // Will use propClinicData (from Viewer) if available, otherwise Platform context
    const resolvedClinicData = clinicData?.clinicData?.clinicName
        ? { ...clinicData.clinicData, source: 'partner' }
        : {
              clinicName: userData.clinicName || '',
              url: userData.clinicImageId || '',
              emailAddress: userData.emailAddress || '',
              clinicNumber: userData.clinicNumber || '',
              accountType: subscriptionData.accountType || '',
              showLogo: userData.showLogoInHandouts || false,
              source: 'platform',
          };

    // same variable like in Patient UI
    const isPartner = resolvedClinicData.source === 'partner';
    const hasContactInfo = !!resolvedClinicData.emailAddress || !!resolvedClinicData?.clinicNumber;
    // Show contact message if clinic name exists AND at least one contact method exists
    const showContactBlock = !!resolvedClinicData.clinicName && hasContactInfo;

    // Logo visibility logic:
    // - Partner mode: Show logo if URL exists
    // - Platform mode with contact message: Show logo if URL exists (ignore showLogo setting)
    // - Platform mode without contact message: Respect showLogo setting
    const hasLogo = isPartner
        ? !!resolvedClinicData.url
        : showContactBlock
          ? !!resolvedClinicData.url // Show logo when contact message is shown
          : resolvedClinicData.showLogo === true && !!resolvedClinicData.url; // Respect showLogo setting otherwise

    // Show clinic name fallback ONLY if no logo AND contact message is NOT shown
    const showClinicNameFallback = resolvedClinicData.clinicName && !hasLogo && !showContactBlock;

    // Debug logging
    console.warn('[Platform Copyright] resolvedClinicData:', resolvedClinicData);
    console.warn(
        '[Platform Copyright] isPartner:',
        isPartner,
        'hasLogo:',
        hasLogo,
        'showContactBlock:',
        showContactBlock,
    );

    return (
        <>
            {/* Contact block - shown for both Partner and Platform when clinic name and contact info exist */}
            {showContactBlock && (
                <Box
                    component="footer-top"
                    sx={{
                        mb: 1,
                        '@media print': { display: 'none' },
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ fontStyle: 'italic', color: '#666666', fontSize: 16, lineHeight: 1.4 }}
                        className="handout-contact"
                    >
                        For any queries, please contact via{' '}
                        {resolvedClinicData?.emailAddress && (
                            <>
                                <Link href={`mailto:${resolvedClinicData.emailAddress}`} underline="always">
                                    {resolvedClinicData.emailAddress}
                                </Link>
                                {resolvedClinicData?.clinicNumber && ' or '}
                            </>
                        )}
                        {resolvedClinicData?.clinicNumber && <>{resolvedClinicData.clinicNumber}</>}.
                    </Typography>
                </Box>
            )}

            {/* Always show footer */}
            <Box
                component="footer"
                sx={{
                    pt: 1,
                    textAlign: 'left',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '@media print': {
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        bgcolor: 'background.paper',
                        zIndex: 0,
                        overflow: 'visible',
                        py: 0,
                        display: 'none',
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        lineHeight: '1.5',
                        fontSize: 12,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'center',
                        flex: 1,
                        '@media print': { fontSize: '6pt', marginBottom: '-9px' },
                    }}
                >
                    © Copyright Cetra Health. All rights reserved.
                </Typography>

                {/* Show logo or clinic name fallback — flex-positioned so they never overlap copyright */}
                {resolvedClinicData && (
                    <>
                        {/* Show logo if available (respects showLogo setting for Platform) */}
                        {hasLogo && (
                            <img
                                src={resolvedClinicData.url}
                                alt="Clinic Logo"
                                style={{
                                    maxHeight: 75,
                                    maxWidth: 280,
                                    objectFit: 'contain',
                                    flexShrink: 0,
                                    marginLeft: 8,
                                }}
                            />
                        )}
                        {/* Show clinic name fallback for Platform mode only */}
                        {!isPartner && showClinicNameFallback && (
                            <Box sx={{ fontSize: 16, flexShrink: 0, ml: 1 }}>
                                {resolvedClinicData.clinicName}
                            </Box>
                        )}
                        {!hasLogo && onUploadLogoClick && (
                            <Box
                                onClick={onUploadLogoClick}
                                sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'row' : 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: isMobile ? 0 : '2px',
                                    color: '#FF669B',
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    ml: 1,
                                }}
                            >
                                <Box component="img" src={LogoUploadIcon} alt="Upload logo" sx={{ width: 24, height: 24 }} />
                                {!isMobile && <Box sx={{ textDecoration: 'underline' }}>Upload logo</Box>}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </>
    );
};

export default Copyright;
